import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCourseGenerationRequest } from "@/hooks/useCourseGenerationRequest";
import { ContentGrid } from "@/components/content/ContentGrid";
import { ContentItem, ContentType } from "@/types/content";
import { Skeleton } from "@/components/ui/skeleton";
import { MessageSquareText, FileText, Link2, Sparkles, Upload } from "lucide-react";

interface CourseSourcesViewProps {
  courseId: string;
  sourceContentIds: string[];
}

const sourceTypeConfig: Record<string, { icon: React.ElementType; label: string; description: string }> = {
  prompt: {
    icon: MessageSquareText,
    label: "Text Prompt",
    description: "This course was generated from a text prompt.",
  },
  paste: {
    icon: FileText,
    label: "Pasted Text",
    description: "This course was generated from pasted text content.",
  },
  content: {
    icon: Link2,
    label: "Linked Content",
    description: "This course was generated from a linked content item.",
  },
  upload: {
    icon: Upload,
    label: "Uploaded File",
    description: "This course was generated from an uploaded file.",
  },
};

export const CourseSourcesView: React.FC<CourseSourcesViewProps> = ({
  courseId,
  sourceContentIds,
}) => {
  const { data: genRequest, isLoading: genLoading } = useCourseGenerationRequest(courseId);
  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");

  // Merge sourceContentIds with genRequest.contentId to ensure we catch all linked content
  const allContentIds = React.useMemo(() => {
    const ids = new Set(sourceContentIds);
    genRequest?.contentIds?.forEach((id) => {
      if (id) ids.add(id);
    });
    if (genRequest?.contentId) {
      ids.add(genRequest.contentId);
    }
    return Array.from(ids);
  }, [sourceContentIds, genRequest?.contentId, genRequest?.contentIds]);

  // Fetch linked content items
  const { data: linkedContent, isLoading: contentLoading } = useQuery({
    queryKey: ["course-source-content", courseId, allContentIds],
    queryFn: async () => {
      if (allContentIds.length === 0) return [];
      const { data, error } = await supabase
        .from("content")
        .select("*")
        .in("id", allContentIds);
      if (error) throw error;
      return (data ?? []).map(
        (item): ContentItem => ({
          id: item.id,
          title: item.title,
          type: item.type as ContentType,
          createdAt: item.created_at,
          thumbnailUrl: item.thumbnail_url,
          sourceUrl: item.source_url,
          storagePath: item.storage_path,
          roomId: item.room_id,
          updatedAt: item.updated_at,
          visibility: item.visibility as "private" | "public",
        }),
      );
    },
    enabled: allContentIds.length > 0 || (genRequest !== undefined && !genLoading),
  });

  if (genLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
      </div>
    );
  }

  const sourceKey = genRequest?.source ?? "";
  const config = sourceTypeConfig[sourceKey] ?? {
    icon: Sparkles,
    label: "the below prompt text",
    description: "This course was generated by AI.",
  };
  const hasLinkedContent = allContentIds.length > 0;

  return (
    <div className="p-4 sm:p-6">
      {/* Generation source card */}
      <div className="rounded-lg border border-border/60 bg-card p-4">
  <div className="flex items-center gap-2.5">
    <div className="flex items-center gap-1.5 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">
        Generated from {config.label}
      </p>
    </div>
  </div>

  {genRequest?.prompt && (
    <div className="mt-3">
      <div className="rounded-md bg-muted/40 border border-border/50 px-3 py-2.5">
        <p className="text-xs text-muted-foreground/60 font-medium uppercase tracking-widest mb-1">
          Prompt
        </p>
        <p className="text-sm text-foreground/80 leading-relaxed line-clamp-4">
          {genRequest.prompt}
        </p>
      </div>
    </div>
  )}

  {!genRequest && (
    <p className="mt-2 text-sm text-muted-foreground/60">
      No generation details available.
    </p>
  )}
</div>
      {/* Linked content items */}
      {hasLinkedContent && (
        <section className="mt-6 flex w-full flex-col items-center justify-center md:block">
          {contentLoading ? (
            <div className="flex w-full flex-col items-center justify-center md:block">
              <div className="w-full" />
              <div className="flex w-full items-center justify-end text-muted-foreground md:mb-3 md:justify-between">
                <div className="inline-flex h-10 items-center justify-center rounded-full bg-muted px-2 text-muted-foreground md:order-2 md:ml-auto md:px-1.5">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      viewMode === "grid"
                        ? "bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-state={viewMode === "grid" ? "active" : "inactive"}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      viewMode === "list"
                        ? "bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-state={viewMode === "list" ? "active" : "inactive"}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M4 6h16" />
                      <path d="M4 12h16" />
                      <path d="M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="h-full w-full mt-4 md:mt-0">
                <div className="grid gap-8 md:mt-6 md:gap-4">
                  <ContentGrid
                    items={[]}
                    isLoading
                    skeletonCount={allContentIds.length}
                    viewMode={viewMode}
                  />
                </div>
              </div>
            </div>
          ) : linkedContent && linkedContent.length > 0 ? (
            <div className="flex w-full flex-col items-center justify-center md:block">
              <div className="w-full" />
              <div className="flex w-full items-center justify-end text-muted-foreground md:mb-3 md:justify-between">
                <div className="inline-flex h-10 items-center justify-center rounded-full bg-muted px-2 text-muted-foreground md:order-2 md:ml-auto md:px-1.5">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      viewMode === "grid"
                        ? "bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-state={viewMode === "grid" ? "active" : "inactive"}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      viewMode === "list"
                        ? "bg-background text-foreground data-[state=active]:bg-background data-[state=active]:text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-state={viewMode === "list" ? "active" : "inactive"}
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M4 6h16" />
                      <path d="M4 12h16" />
                      <path d="M4 18h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="h-full w-full mt-4 md:mt-0">
                <div className="grid gap-8 md:mt-6 md:gap-4">
                  <ContentGrid
                    items={linkedContent}
                    isLoading={false}
                    skeletonCount={allContentIds.length}
                    viewMode={viewMode}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="mb-3 h-7 w-7 text-muted-foreground/40" />
              <h3 className="mb-2 text-xl font-semibold text-foreground">
                Source content not accessible
              </h3>
              <p className="max-w-md text-muted-foreground">
                The original file may have been deleted or is unavailable.
              </p>
            </div>
          )}
        </section>
      )}

      {/* Empty state — no content linked at all */}
      {!hasLinkedContent && !genRequest?.contentId && (
        <div className="mt-6 flex flex-col items-center justify-center py-16 text-center">
          <Sparkles className="mb-3 h-8 w-8 text-muted-foreground/40" />
          <h3 className="mb-2 text-xl font-semibold text-foreground">
            No source materials yet
          </h3>
          <p className="max-w-md text-muted-foreground">
            This course was generated without reference content.
          </p>
        </div>
      )}
    </div>
  );
};
